import { format } from 'date-fns';
import { AlertTriangle, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '../components/Badge';
import { type Column, DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import {
  type AuditCatalogEntry,
  type AuditLevel,
  type AuditLogEntry,
  type ChatAuditEntry,
  useAuditLogCatalog,
  useAuditLogs,
  useChatAuditLog,
} from '../hooks/useAdminData';
import { useDebounce } from '../hooks/useDebounce';

function targetTypeBadgeVariant(type: string): 'default' | 'info' | 'primary' {
  switch (type.toLowerCase()) {
    case 'subscription':
      return 'info';
    case 'user':
      return 'primary';
    default:
      return 'default';
  }
}

function levelBadgeVariant(level: AuditLevel): 'danger' | 'warning' | 'info' {
  switch (level) {
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    default:
      return 'info';
  }
}

const SOURCE_LABELS: Record<AuditCatalogEntry['source'], string> = {
  business_event: 'Business Events',
  activity: 'Lead Activity',
  admin: 'Admin Actions',
};

const LEVEL_OPTIONS: { value: '' | AuditLevel; label: string }[] = [
  { value: '', label: 'All levels' },
  { value: 'error', label: 'Errors' },
  { value: 'warning', label: 'Warnings' },
  { value: 'info', label: 'Info' },
];

function truncateId(id: string) {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...`;
}

function extractMessage(details: Record<string, unknown>): string | null {
  for (const key of ['message', 'error', 'reason', 'detail']) {
    const value = details[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return null;
}

const PAGE_SIZE = 20;

export function AuditLog() {
  const [view, setView] = useState<'platform' | 'chat'>('platform');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [targetType, setTargetType] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [level, setLevel] = useState<'' | AuditLevel>('');
  const [showCatalog, setShowCatalog] = useState(true);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [detailEntry, setDetailEntry] = useState<AuditLogEntry | null>(null);

  const debouncedAction = useDebounce(actionFilter, 300);

  const { data: catalog = [], isLoading: catalogLoading } = useAuditLogCatalog();

  const catalogBySource = useMemo(() => {
    const filtered = level ? catalog.filter((e) => e.level === level) : catalog;
    const groups: Record<AuditCatalogEntry['source'], AuditCatalogEntry[]> = {
      business_event: [],
      activity: [],
      admin: [],
    };
    for (const entry of filtered) groups[entry.source].push(entry);
    return groups;
  }, [catalog, level]);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, limit: PAGE_SIZE };
    if (dateFrom) p.startDate = dateFrom;
    if (dateTo) p.endDate = dateTo;
    if (targetType) p.targetType = targetType;
    if (level) p.level = level;
    if (debouncedAction) p.action = debouncedAction;
    if (sortBy) {
      p.sortBy = sortBy;
      p.sortDir = sortDir;
    }
    return p;
  }, [page, dateFrom, dateTo, targetType, level, debouncedAction, sortBy, sortDir]);

  const { data, isLoading } = useAuditLogs(params);

  function handleSort(key: string, direction: 'asc' | 'desc') {
    setSortBy(key);
    setSortDir(direction);
    setPage(1);
  }

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'createdAt',
      header: 'Timestamp',
      sortable: true,
      render: (row) => (
        <span className="text-text-secondary">
          {format(new Date(row.createdAt), 'MMM d, yyyy HH:mm')}
        </span>
      ),
    },
    {
      key: 'adminName',
      header: 'Admin',
      sortable: true,
      render: (row) => <span className="text-text font-medium">{row.adminName}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (row) => <span className="text-text font-medium">{row.action}</span>,
    },
    {
      key: 'level',
      header: 'Level',
      render: (row) => <Badge variant={levelBadgeVariant(row.level)}>{row.level}</Badge>,
    },
    {
      key: 'targetType',
      header: 'Target Type',
      render: (row) => (
        <Badge variant={targetTypeBadgeVariant(row.targetType)}>{row.targetType}</Badge>
      ),
    },
    {
      key: 'targetId',
      header: 'Target ID',
      render: (row) => (
        <span className="font-mono text-xs text-text-secondary" title={row.targetId}>
          {truncateId(row.targetId)}
        </span>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDetailEntry(row);
          }}
          className="px-2.5 py-1 text-xs rounded-md border border-border text-text-secondary hover:bg-surface-hover hover:text-text transition-colors"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Audit Log</h1>

      <div className="flex items-center gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setView('platform')}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            view === 'platform'
              ? 'border-primary text-text'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          Platform Actions
        </button>
        <button
          type="button"
          onClick={() => setView('chat')}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            view === 'chat'
              ? 'border-primary text-text'
              : 'border-transparent text-text-muted hover:text-text'
          }`}
        >
          Chat Actions
        </button>
      </div>

      {view === 'chat' ? (
        <ChatAuditTab />
      ) : (
        <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        <select
          value={level}
          onChange={(e) => {
            setLevel(e.target.value as '' | AuditLevel);
            setPage(1);
          }}
          aria-label="Filter by level"
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          {LEVEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={targetType}
          onChange={(e) => {
            setTargetType(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All Types</option>
          <option value="organization">Organization</option>
          <option value="organization_member">Organization Member</option>
          <option value="subscription">Subscription</option>
          <option value="user">User</option>
        </select>

        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Filter by action..."
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <button
          type="button"
          onClick={() => setShowCatalog((s) => !s)}
          aria-expanded={showCatalog}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <span className="text-sm font-medium text-text">
            Audit log types{level ? ` · ${level}` : ''}
          </span>
          <span className="text-xs text-text-muted">{showCatalog ? 'Hide' : 'Show'}</span>
        </button>

        {showCatalog && (
          <div className="border-t border-border p-4 space-y-4">
            {catalogLoading && <span className="text-sm text-text-muted">Loading types…</span>}
            {!catalogLoading &&
              (Object.keys(SOURCE_LABELS) as AuditCatalogEntry['source'][]).map((source) => {
                const entries = catalogBySource[source];
                if (entries.length === 0) return null;
                return (
                  <div key={source} className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      {SOURCE_LABELS[source]} ({entries.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {entries.map((entry) => (
                        <span
                          key={`${source}:${entry.key}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1"
                        >
                          <Badge variant={levelBadgeVariant(entry.level)}>{entry.level}</Badge>
                          <span className="font-mono text-xs text-text-secondary">{entry.key}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            {!catalogLoading &&
              (Object.keys(SOURCE_LABELS) as AuditCatalogEntry['source'][]).every(
                (s) => catalogBySource[s].length === 0,
              ) && <span className="text-sm text-text-muted">No types match this level.</span>}
          </div>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (AuditLogEntry & Record<string, unknown>)[]}
          total={data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onSort={handleSort}
          onRowClick={(row) => setDetailEntry(row as unknown as AuditLogEntry)}
          loading={isLoading}
        />
      </div>

      <Modal
        open={!!detailEntry}
        onClose={() => setDetailEntry(null)}
        title="Audit entry"
        size="lg"
      >
        {detailEntry &&
          (() => {
            const message = extractMessage(detailEntry.details);
            return (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={levelBadgeVariant(detailEntry.level)}>{detailEntry.level}</Badge>
                  <span className="font-mono text-sm text-text">{detailEntry.action}</span>
                </div>

                {detailEntry.level === 'error' && message && (
                  <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{message}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <span className="text-xs text-text-muted">Admin</span>
                    <div className="text-text">{detailEntry.adminName}</div>
                  </div>
                  <div>
                    <span className="text-xs text-text-muted">When</span>
                    <div className="text-text">
                      {format(new Date(detailEntry.createdAt), 'MMM d, yyyy HH:mm:ss')}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-text-muted">Target Type</span>
                    <div className="text-text">{detailEntry.targetType}</div>
                  </div>
                  <div>
                    <span className="text-xs text-text-muted">Target ID</span>
                    <div className="font-mono text-xs text-text break-all">
                      {detailEntry.targetId}
                    </div>
                  </div>
                </div>

                <div>
                  <span className="text-xs text-text-muted">Full details</span>
                  <pre className="mt-1 bg-background rounded p-3 text-xs font-mono text-text-secondary overflow-x-auto max-h-80">
                    {JSON.stringify(detailEntry.details, null, 2)}
                  </pre>
                </div>
              </div>
            );
          })()}
      </Modal>
        </>
      )}
    </div>
  );
}

function chatAuditRowVariant(status: string): 'success' | 'danger' | 'warning' | 'info' | 'default' {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'approved':
      return 'success';
    case 'failed':
    case 'rejected':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

function chatAuditTypeLabel(type: ChatAuditEntry['type']): string {
  return type === 'approval_resolution' ? 'Approval' : 'Tool Call';
}

function ChatAuditTab() {
  const { data, isLoading } = useChatAuditLog();
  const entries = data?.data ?? [];

  return (
    <div className="bg-surface rounded-xl border border-border">
      <div className="px-5 py-4 border-b border-border">
        <h2 className="text-sm font-medium text-text-secondary">
          Chat orchestrator tool calls and approval resolutions
        </h2>
        <p className="text-xs text-text-muted mt-1">
          Chronological view over the agent_executions ledger (agentType=orchestrator) and
          resolved tool approvals, from GET /admin/chat/audit-log.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th className="px-5 py-3 font-medium">Timestamp</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">Tool</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Organization</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            ) : entries.length > 0 ? (
              entries.map((entry) => (
                <tr
                  key={`${entry.type}:${entry.id}`}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-5 py-3 text-text-secondary">
                    {format(new Date(entry.timestamp), 'MMM d, yyyy HH:mm:ss')}
                  </td>
                  <td className="px-5 py-3 text-text-secondary">{chatAuditTypeLabel(entry.type)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-text">
                    {entry.toolName ?? '-'}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={chatAuditRowVariant(entry.status)}>{entry.status}</Badge>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-text-secondary">
                    {entry.organizationId}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                  No chat actions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
